import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import _ from 'underscore';
import moment from 'moment';
import useRouter from 'utils/useRouter';
import { makeStyles } from '@mui/styles';
import {
	colors,
	IconButton,
	Tooltip,
} from '@mui/material';

import CreateIcon from '@mui/icons-material/Create';
import EmailIcon from '@mui/icons-material/Email';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import DeleteIcon from '@mui/icons-material/Delete';
import { Label, DataTable } from 'components';
import { DeleteTicket } from '../../../../actions/TicketsActions';

const useStyles = makeStyles((theme) => ({
	root: {},
	content: {
		padding: 0,
	},
	inner: {
		minWidth: 700,
	},
	nameCell: {
		display: 'flex',
		alignItems: 'center',
	},
	avatar: {
		height: 42,
		width: 42,
		marginRight: theme.spacing(1),
	},
	actions: {
		padding: theme.spacing(1),
		justifyContent: 'flex-end',
	},
	primaryButton: {
		color: theme.palette.primary.main
	},
	errorButton: {
		color: theme.palette.error.main
	}
}));

const TicketsDataTable = (props) => {
	const { className, data, ...rest } = props;
	const dispatch = useDispatch();
	const classes = useStyles();
	const { history } = useRouter();
	const user = useSelector(state => state.session.user);

	const _details = (ticketId) => {
		history.push(`/tickets/details/${ticketId}`);
	};

	const _delete = (ticketId) => {
		dispatch(DeleteTicket(ticketId));
	};

	const _renderActions = (tableMeta) => {
		const { rowData } = tableMeta;
		const ticket = _.findWhere(data, { id: rowData[0] });

		return (
			<>
				<Tooltip title="Details">
					<IconButton edge="end" size="small" className={classes.primaryButton} onClick={() => _details(rowData[0])}>
						<CreateIcon />
					</IconButton>
				</Tooltip>
				<Tooltip title="Delete">
					<IconButton disabled={!_.isEqual(ticket.userId, user.id)} edge="end" size="small" className={classes.errorButton} onClick={() => _delete(rowData[0])}>
						<DeleteIcon />
					</IconButton>
				</Tooltip>
			</>

		);
	};

	const _getTableColumns = () => (
		[
			{
				name: 'id',
				label: 'Id',
				options: {
					display: false,
					filter: false,
					viewColumns: false,
					filterType: 'textField',
				}
			},
			{
				name: 'subject',
				label: 'Subject',
				options: {
					display: true,
					filterType: 'textField',
				}
			},
			{
				name: 'activityName',
				label: 'Activity',
				options: {
					display: true,
					filterType: 'textField',
				}
			},
			{
				name: 'typeName',
				label: 'Type',
				options: {
					display: true,
					filterType: 'textField',
				}
			},
			{
				name: 'targetExecutionDate',
				label: 'Target Execution Date',
				options: {
					display: true,
					filterType: 'textField',
					customBodyRender: (value, tableMeta, updateValue) => <>{moment(value).format('DD-MM-YYYY HH:mm:ss').toString()}</>
				}
			},
			{
				name: 'teamName',
				label: 'Team',
				options: {
					display: true,
					filterType: 'textField',
				}
			},
			{
				name: 'stageName',
				label: 'Stage',
				options: {
					display: true,
					filterType: 'textField',
				}
			},
			{
				name: 'action',
				label: 'Actions',
				options: {
					filter: false,
					sort: false,
					viewColumns: false,
					empty: true,
					customBodyRender: (value, tableMeta, updateValue) => _renderActions(tableMeta)
				}
			},
		]
	);

	const _getTableOptions = () => ({
		filter: false,
		selectableRows: false,
		expandableRows: false, // Try Adding This
	});

	return (
		<div {...rest} className={clsx(classes.root, className)}>
			<DataTable
				id="ticketsDataTable"
				title="Tickets"
				data={data}
				columns={_getTableColumns()}
				rowsPerPage={10}
				tableBodyMaxHeight="30em"
				tableBodyCellMaxWidth="250"
				options={_getTableOptions()}
			/>
		</div>
	);
};

TicketsDataTable.propTypes = {
	className: PropTypes.string,
	data: PropTypes.array.isRequired,
};

export default TicketsDataTable;
